import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InterpreterRecordingAudio } from './recordingAudio';

export const useInterpreterAudio = (id: string | undefined, mounted: RefObject<boolean>) => {
  const recordingAudio = useMemo(() => new InterpreterRecordingAudio(id ?? ''), [id]);
  const currentAudio = useRef(recordingAudio);
  currentAudio.current = recordingAudio;
  const [audioError, setAudioError] = useState(false);
  const [audioRejected, setAudioRejected] = useState(false);
  const [audioSaving, setAudioSaving] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const saveAudio = async () => {
    if (mounted.current) setAudioSaving(true);
    try {
      await recordingAudio.flush();
      if (mounted.current && currentAudio.current === recordingAudio) {
        setAudioError(false);
        setAudioRejected(recordingAudio.rejected);
        if (recordingAudio.saved) setAudioUrl(`/api/sessions/${id}/audio?t=${Date.now()}`);
      }
      return true;
    } catch {
      if (mounted.current && currentAudio.current === recordingAudio) setAudioError(true);
      return false;
    } finally {
      if (mounted.current && currentAudio.current === recordingAudio) setAudioSaving(false);
    }
  };
  useEffect(() => {
    setAudioUrl(recordingAudio.saved ? `/api/sessions/${id}/audio` : null);
    setAudioError(false);
    setAudioRejected(recordingAudio.rejected);
    setAudioSaving(false);
  }, [recordingAudio, id]);
  const prepareAudio = async () => {
    await recordingAudio.prepare();
    if (mounted.current && currentAudio.current === recordingAudio) {
      setAudioError(false);
      setAudioRejected(recordingAudio.rejected);
      if (recordingAudio.saved) setAudioUrl(`/api/sessions/${id}/audio?t=${Date.now()}`);
    }
  };
  const restoreAudio = useCallback(() => setAudioUrl(`/api/sessions/${id}/audio`), [id]);
  return { recordingAudio, audioError, audioRejected, audioSaving, audioUrl, saveAudio, restoreAudio, prepareAudio };
};
